import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import useModal from '@/shared/hooks/useModal'
import { updateScheduledCallback } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { scheduleCallbackModuleActions } from '@/containers/SubscriptionFormPageNew/Components/ScheduleCallback/ScheduleCallbackSagas'
import {
  selectScheduledCallbackState,
  selectScheduledCallbackForm,
  selectPersonalContactSubscriptionData,
  selectScheduledCallbackUpcoming,
  selectHasScheduledCallbackUpcoming,
  selectActiveTabType,
  selectConfig,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'

import {
  toIsoDate,
  sameYMD,
  parseHM,
  buildHourOptions as buildHourOptionsDT,
  buildMinuteOptions as buildMinuteOptionsDT,
} from '@/shared/tools/dateTime'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'

import {
  BUSINESS_VERTICAL_ENERGY,
  BUSINESS_VERTICAL_INSURANCE,
  BUSINESS_VERTICAL_TELECOM,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'

const useScheduleCallback = () => {
  const openingHours = { start: '07:00', end: '20:00', minuteStep: 10 }
  const { enqueue } = useSnackbar()
  const dispatch = useDispatch()

  const sourceClientIdSelector = selectConfig

  const sourceClientIdData = useSelector(sourceClientIdSelector) || {}
  const clientId = sourceClientIdData?.contactId

  const sourceClientSelector = selectPersonalContactSubscriptionData

  const client = useSelector(sourceClientSelector) || {}
  const { zohoId } = client || ''

  const activeTabType = useSelector(selectActiveTabType) || ''
  const businessVertical = String(activeTabType).toLowerCase()

  const scheduled = useSelector(selectScheduledCallbackState)
  const { firstName, lastName, phone, email, scheduledDate, hour, minutes, assigned, isValid } =
    useSelector(selectScheduledCallbackForm)
  const upcoming = useSelector(selectScheduledCallbackUpcoming)
  const hasUpcoming = useSelector(selectHasScheduledCallbackUpcoming)
  const isLoading = scheduled.loading
  const success = scheduled.success
  const message = scheduled.message || ''
  const googleCalendarUrl = scheduled.googleCalendarUrl || null
  const { country: rawCountry } = useSelector(selectAuthData)
  const country = String(rawCountry || '').toLowerCase()

  const triggerTextKey = hasUpcoming
    ? 'subscriptionTools:schedule_callback.already_scheduled_callback'
    : 'subscriptionTools:schedule_callback.schedule_button'

  const popOverTitleKey = hasUpcoming
    ? 'subscriptionTools:schedule_callback.already_scheduled_callback'
    : 'subscriptionTools:schedule_callback.click_to_scheduled_callback'

  const defaults = {
    firstName: client?.firstName || '',
    lastName: client?.lastName || '',
    phone: client?.phone1 || client?.phone || '',
    email: client?.email || '',
  }

  const { hour: openingStartHour, minute: openingStartMinute } = parseHM(openingHours.start)
  const { hour: openingEndHour } = parseHM(openingHours.end)
  const minuteStep = openingHours.minuteStep || 10

  const now = new Date()
  const selectedDate = scheduledDate ? new Date(scheduledDate) : new Date()
  const dateValue = [selectedDate]
  const isToday = sameYMD(selectedDate, now)

  const buildHourOptions = () => {
    return buildHourOptionsDT({
      startHour: openingStartHour,
      endHour: openingEndHour,
      isToday,
      now,
    })
  }

  const buildMinuteOptions = currentHour => {
    return buildMinuteOptionsDT({
      currentHour,
      startHour: openingStartHour,
      startMinute: openingStartMinute,
      isToday,
      now,
      minuteStep,
    })
  }

  const hourOptions = buildHourOptions()
  const minuteOptions = buildMinuteOptions(hour)

  const setFirstName = newFirstName => dispatch(updateScheduledCallback({ form: { firstName: newFirstName } }))
  const setLastName = newLastName => dispatch(updateScheduledCallback({ form: { lastName: newLastName } }))
  const setPhone = newPhone => dispatch(updateScheduledCallback({ form: { phone: newPhone } }))
  const setEmail = newEmail => dispatch(updateScheduledCallback({ form: { email: newEmail } }))
  const setDate = dateOrArray => {
    const dateValueLocal = Array.isArray(dateOrArray) ? dateOrArray[0] : dateOrArray
    const iso =
      dateValueLocal instanceof Date && !Number.isNaN(dateValueLocal.getTime()) ? toIsoDate(dateValueLocal) : ''
    dispatch(updateScheduledCallback({ form: { scheduledDate: iso } }))
  }
  const setHour = newHour => dispatch(updateScheduledCallback({ form: { hour: String(newHour) } }))
  const setMinutes = newMinutes => dispatch(updateScheduledCallback({ form: { minutes: String(newMinutes) } }))
  const setAssigned = assignedValue => dispatch(updateScheduledCallback({ form: { assigned: assignedValue } }))

  const buildPayload = () => {
    const hourPadded = String(hour).padStart(2, '0')
    const minutePadded = String(minutes).padStart(2, '0')
    return {
      contactFirstName: (firstName || '').trim(),
      contactLastName: (lastName || '').trim(),
      contactPhone: (phone || '').trim(),
      contactEmail: (email || '').trim() || null,
      scheduledDate,
      scheduledTime: `${hourPadded}:${minutePadded}`,
      assignedToAgent: assigned === 'self',
      source: 'hop',
      businessType: businessVertical,
    }
  }

  const applyDefaults = defaultsValue => {
    dispatch(
      updateScheduledCallback({
        loading: false,
        success: false,
        message: null,
        error: null,
        lastCreatedAt: null,
        form: {
          firstName: defaultsValue?.firstName || '',
          lastName: defaultsValue?.lastName || '',
          phone: defaultsValue?.phone || '',
          email: defaultsValue?.email || '',
          scheduledDate: toIsoDate(new Date()),
          hour: '',
          minutes: '',
          assigned: 'self',
        },
      }),
    )
  }

  const applyContactDefaults = defaultsValue => {
    dispatch(
      updateScheduledCallback({
        form: {
          firstName: defaultsValue?.firstName || '',
          lastName: defaultsValue?.lastName || '',
          phone: defaultsValue?.phone || '',
          email: defaultsValue?.email || '',
        },
      }),
    )
  }

  const openModal = setOpen => {
    applyContactDefaults(defaults || {})
    if (!hasUpcoming && (!scheduledDate || hour === '' || minutes === '')) {
      applyDefaults(defaults || {})
    }
    if (typeof setOpen === 'function') setOpen(true)
  }

  const schedule = () => {
    if (!isValid || isLoading || hasUpcoming) return
    dispatch({
      type: scheduleCallbackModuleActions.CREATE_SCHEDULE_CALLBACK,
      enqueue,
      payload: buildPayload(),
      zohoContactId: zohoId,
      clientId,
      version: 'v2',
    })
  }

  const { isOpen, close, setIsOpen } = useModal()
  const ALLOWED_BUSINESS_VERTICALS = [BUSINESS_VERTICAL_ENERGY, BUSINESS_VERTICAL_TELECOM, BUSINESS_VERTICAL_INSURANCE]
  const isAllowedBusinessVertical = ALLOWED_BUSINESS_VERTICALS.includes(String(businessVertical || '').toLowerCase())
  const isAllowedCountry = modulesConfig[country]?.menu?.callbacks ?? false
  const isAllowedByVerticalAndCountry = isAllowedBusinessVertical && isAllowedCountry

  return {
    isLoading,
    success,
    message,
    schedule,
    openModal,
    hourOptions,
    minuteOptions,
    firstName,
    lastName,
    phone,
    email,
    date: dateValue,
    hour,
    minutes,
    assigned,
    isValid,
    setFirstName,
    setLastName,
    setPhone,
    setEmail,
    setDate,
    setHour,
    setMinutes,
    setAssigned,
    isOpen,
    close,
    setIsOpen,
    zohoId,
    hasUpcoming,
    upcomingItems: upcoming.items || [],
    googleCalendarUrl,
    triggerTextKey,
    popOverTitleKey,
    businessVertical,
    isAllowedByVerticalAndCountry,
  }
}

export default useScheduleCallback
